import React from 'react'
import { Navigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { LogOut } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useModules } from '../contexts/ModuleContext'
import { prototypeRoles } from '../contexts/moduleVisibility'
import { startCenter } from '../api/client'
import { useFlag } from '../hooks/useFlag'
import ProtectedRoute from './ProtectedRoute'
import { HOME_FLAG } from './layout/workspaceNavigationModel'
import Dashboard from '../pages/Dashboard'
import LoadingIcon from './LoadingIcon'

// Один заброс в стартовый центр на вкладку. Без этого флага каждая перезагрузка
// возвращала бы человека на /start, пока он не закроет шаги, — а бриф прямо
// запрещает блокировать работу в системе до 100% готовности.
const START_CENTER_SEEN_KEY = 'onboardix_start_center_seen'

function PageLoader() {
    return (
        <div
            style={{
                minHeight: '40vh',
                fontSize: '0.938rem'
            }}
            className='flex items-center justify-center color-blue fw-600 gap-8'>
            <LoadingIcon status="PROCESSING" size={20} />Загрузка…
        </div>
    );
}

// Роль → дефолтный лендинг при входе (index route). Каждая роль приходит за
// своим «джобом»: рекрутёр — в воронку, руководитель — к команде, online-
// менеджер — на свой дашборд. Срабатывает только если целевой модуль виден
// роли; иначе падаем на универсальный Dashboard (фолбэк ниже) — пользователя
// никогда не оставляем на пустом экране. Явный /dashboard роле-редирект не
// делает (стабильный bookmarkable URL), см. forceDashboard.
export const ROLE_LANDING = {
    recruiter: 'pipeline',
    dept_head: 'dept-dashboard',
    cmo: 'dept-dashboard',
    head: 'dept-dashboard',
    online_manager: 'manager-dashboard',
    senior_online_manager: 'manager-dashboard',
    // Партнёрские роли заходят в свой «дом». Целимся в partner-home —
    // редирект-компонент (PartnerHomePage), который сам разводит роль по её
    // экрану (менеджер → /partner-desk, руководитель → /partner-team). Так
    // мэппинг роль→дом остаётся в одном месте, а не дублируется здесь.
    partner_manager: 'partner-home',
    head_of_partners: 'partner-home',
    deputy_head_of_partners: 'partner-home',
    // Подрядчик заходит сразу в свой кабинет самообслуживания.
    marketing_contractor: 'contractor-cabinet',
    // Собственник и CEO заходят на панель собственника (#6316) — только при
    // включённом рубильнике (LANDING_FLAGS ниже): выключенный возвращает
    // прежний лендинг, общий дашборд.
    owner: 'owner-panel',
    ceo: 'owner-panel',
}

// Лендинг за рубильником: экран приземления → ключ feature flag.
export const LANDING_FLAGS = {
    'owner-panel': 'owner_panel_v1',
}

// Лендинг за правом: личный запрет права не должен приземлять человека на
// экран, который ответит ему 403 при каждом входе. Пока права не загружены,
// решает видимость раздела — та же, что открывает пункт меню.
export const LANDING_PERMISSIONS = {
    'owner-panel': 'owner_panel.read',
}

// Лендинг человека: сначала свой код роли, затем код РОЛИ-ПРОТОТИПА
// (`custom_roles.base_role`, ADR-108). Своего лендинга у кастомной роли нет и
// быть не может: ROLE_LANDING ключится на встроенные коды, а код кастомной роли
// заводит администратор второй компании. До #5687 развилки здесь не было, и
// носитель роли «ходит как руководитель отдела» после входа падал в общую
// цепочку «дашборд → входящие → кабинет»: дверь (#5633) и пункт меню (#5667)
// его раздел уже открывали, а долистывал до него человек руками каждый вход.
//
// Прототип у роли РОВНО ОДИН — `custom_roles.base_role` это одна колонка, и
// «несколько прототипов» отвергнуты в ADR-108 отдельным пунктом. Поле приходит
// списком, поэтому поведение на списке всё равно зафиксировано: берём первый
// код, у которого лендинг есть, в том порядке, в каком их отдал сервер.
// Объединять нечего — экран приземления один, а выбирать «главный» из двух
// прототипов значило бы рассуждать о наборе, которого нет ни у одной роли.
export function landingFor(user) {
    const own = ROLE_LANDING[user?.role]
    if (own) return own
    return prototypeRoles(user).map(code => ROLE_LANDING[code]).find(Boolean)
}

export default function HomeRedirect({ forceDashboard = false }) {
    const { user, hasPermission, capabilities } = useAuth()
    const { modules, isModuleVisible, loading } = useModules()
    const target = landingFor(user)
    const landingFlag = LANDING_FLAGS[target]
    // Хук вызывается всегда (правила хуков), а ждём его только те, чей лендинг
    // за рубильником: остальные приземляются без лишнего ожидания.
    const { enabled: landingFlagOn, isLoading: landingFlagLoading } = useFlag(landingFlag || 'owner_panel_v1')
    // Главная (`home_v1`) — общий экран приземления для всех ролей: шаги старта
    // и экран роли живут внутри неё, поэтому оба заброса ниже ей уступают.
    const { enabled: homeOn, isLoading: homeLoading } = useFlag(HOME_FLAG)

    // Готовность запрашиваем ПАРАЛЛЕЛЬНО загрузке модулей, а не после неё —
    // иначе вход обрастает вторым последовательным ожиданием. Учётка без
    // личного дела получает 404, и это нормальный ответ, а не ошибка: центра
    // у неё просто нет, поэтому без ретраев.
    const alreadySeen = (() => {
        try { return sessionStorage.getItem(START_CENTER_SEEN_KEY) === '1' } catch { return true }
    })()
    const { data: readiness, isLoading: readinessLoading } = useQuery({
        queryKey: ['start-center', 'me'],
        queryFn: () => startCenter.me(),
        enabled: !forceDashboard && !homeOn && !homeLoading && !alreadySeen && Boolean(user?.employee_id),
        retry: false,
        staleTime: 30_000,
    })

    if (loading) return <PageLoader />

    // Index (вход в систему) — роле-зависимый лендинг. /dashboard
    // (forceDashboard) этот шаг пропускает → стабильный URL общего дашборда.
    if (!forceDashboard) {
        if (homeLoading) return <PageLoader />
        if (homeOn) return <Navigate to="home" replace />
        // Незакрытые обязательные действия перебивают роле-лендинг ровно один
        // раз за вкладку. Это не блокировка: любой пункт меню уводит дальше.
        if (readinessLoading) return <PageLoader />
        if (readiness && readiness.required?.completed < readiness.required?.total) {
            try { sessionStorage.setItem(START_CENTER_SEEN_KEY, '1') } catch { /* private mode */ }
            return <Navigate to="start" replace />
        }

        if (landingFlag && landingFlagLoading) return <PageLoader />
        const landingPermission = LANDING_PERMISSIONS[target]
        const permitted = !landingPermission || !capabilities || hasPermission(landingPermission)
        const landingOpen = (!landingFlag || landingFlagOn) && permitted
        if (target && landingOpen && isModuleVisible(target, user)) {
            return <Navigate to={target} replace />
        }
    }

    // 2026-05-22: дашборд (HR-focused) теперь универсальный — рендерим его
    // для любой роли, у которой модуль dashboard виден (по умолчанию это все,
    // т.к. в module_seed.py allowed_roles=None). Admin попадает через
    // hasPermission('all'), остальные — через явную видимость модуля.
    if (hasPermission?.('all') || isModuleVisible('dashboard', user)) {
        return <ProtectedRoute moduleKey="dashboard"><Dashboard /></ProtectedRoute>
    }

    if (isModuleVisible('inbox', user)) return <Navigate to="inbox" replace />
    if (isModuleVisible('cabinet', user)) return <Navigate to="cabinet" replace />

    const firstVisible = modules.find(m => m.key && m.key !== 'dashboard' && isModuleVisible(m.key, user))
    if (firstVisible) {
        return <Navigate to={firstVisible.key} replace />
    }

    return (
        <div style={{ minHeight: '60vh' }} className='flex flex-col items-center justify-center gap-16'>
            <h2 style={{ margin: 0 }} className='text-rel-2xl fw-700 text-muted'>
                Нет доступных разделов
            </h2>
            <p style={{ margin: 0, maxWidth: 360, lineHeight: 1.5 }} className='text-center text-rel-md text-muted'>
                Для вашей роли еще не настроен доступ к модулям. Обратитесь к администратору для получения прав.
            </p>
            <button
                onClick={() => window.dispatchEvent(new Event('auth:logout'))}
                className='btn btn--secondary btn--sm mt-12 flex items-center gap-8'>
                <LogOut size={16} /> Выйти из аккаунта
            </button>
        </div>
    )
}
